import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  CompleteDocSignRequest,
  CompleteDocSignRequestResponse,
  CreateCustomDocumentRequest,
  CreateCustomDocumentResponse,
  CreateOrUpdateCustomDocumentRequest,
  CreateOrUpdateCustomDocumentResponse,
  GenerateDocSignRequest,
  GenerateDocSignRequestResponse,
  GetCustomDocumentSettingsRequest,
  GetCustomDocumentSettingsResponse,
  GetDocSignRequest,
  GetDocSignRequestResponse,
  UpdateDocumentStatusRequest,
  UpdateDocumentStatusResponse,
  CreateDoctSendSignRequest,
} from "../../models/DocumentSettings.model";
import { environment } from "../../environments";

const API_URL = environment.apiUrl;

// Thunk for creating and downloading a custom document
export const createCustomDownloadDocument = createAsyncThunk<
  { success: boolean; message: string; customDocument?: any },
  CreateCustomDocumentRequest,
  { rejectValue: string }
>(
  "documentSettings/createCustomDownloadDocument",
  async (request, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${API_URL}Document/CreateCustomDownloadDocument`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            accept: "*/*",
          },
          body: JSON.stringify(request),
        },
      );

      if (!response.ok) {
        return rejectWithValue(`שגיאת שרת: ${response.status}`);
      }

      // Try to read created document metadata from response headers (if backend includes it)
      const createdHeader =
        response.headers.get("X-Created-Document") ||
        response.headers.get("x-created-document");
      let createdDocument: any = undefined;
      if (createdHeader) {
        // backend may return plain ID string or JSON. handle both.
        const trimmed = createdHeader.trim();
        // numeric ID
        if (/^-?\d+$/.test(trimmed)) {
          createdDocument = { ProjectDocument_ID: Number(trimmed) };
        } else {
          try {
            createdDocument = JSON.parse(createdHeader);
          } catch (e) {
            // ignore parse errors
          }
        }
      }

      const blob = await response.blob();

      if (blob.size === 0) {
        return rejectWithValue("לא התקבל קובץ מהשרת");
      }

      // קריאת שם הקובץ מה-header (כמו בייצוא דוחות)
      const contentDisposition =
        response.headers.get("content-disposition") ||
        response.headers.get("Content-Disposition");
      let fileName = "";
      if (contentDisposition) {
        // תמיכה ב-UTF-8 encoded filenames (RFC 2231)
        const utf8Match = contentDisposition.match(
          /filename\*=UTF-8''(.+?)(?:;|$)/i,
        );
        if (utf8Match && utf8Match[1]) {
          fileName = decodeURIComponent(utf8Match[1]);
        } else {
          // fallback לשם רגיל
          const filenameMatch = contentDisposition.match(
            /filename=["']?([^"';]+)["']?/i,
          );
          if (filenameMatch && filenameMatch[1]) {
            fileName = filenameMatch[1];
          }
        }
      }
      // אם לא הצלחנו לקבל שם קובץ מה-header, צור שם דיפולטי
      if (!fileName) {
        const extension = "pdf";
        const timestamp = new Date()
          .toISOString()
          .replace(/[:.]/g, "-")
          .slice(0, -5);
        fileName = `document_${timestamp}.${extension}`;
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return {
        success: true,
        message: "המסמך הורד בהצלחה",
        customDocument: createdDocument,
      };
    } catch (error: any) {
      return rejectWithValue(error.message || "שגיאה בחיבור לשרת");
    }
  },
);

export const getCustomDocumentSettings = createAsyncThunk<
  GetCustomDocumentSettingsResponse,
  GetCustomDocumentSettingsRequest,
  { rejectValue: string }
>(
  "documentSettings/getCustomDocumentSettings",
  async (request, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${API_URL}Document/GetCustomDocumentSettings`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            accept: "*/*",
          },
          body: JSON.stringify(request),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        return rejectWithValue(
          errorData.message || errorData.details || "שגיאה בקבלת הגדרות מסמכים",
        );
      }

      const data = await response.json();

      if (!data.success) {
        return rejectWithValue(data.message || "שגיאה בקבלת הגדרות מסמכים");
      }

      return data as GetCustomDocumentSettingsResponse;
    } catch (error: any) {
      return rejectWithValue(error.message || "שגיאה בחיבור לשרת");
    }
  },
);

// 🆕 Thunk ליצירה/עדכון שבלונות מסמכים
export const createOrUpdateDocumentTemplate = createAsyncThunk<
  CreateOrUpdateCustomDocumentResponse,
  CreateOrUpdateCustomDocumentRequest,
  { rejectValue: string }
>(
  "documentSettings/createOrUpdateDocumentTemplate",
  async (request, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${API_URL}Document/CreateOrUpdateBaseCustomDocument`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            accept: "*/*",
          },
          body: JSON.stringify(request),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        return rejectWithValue(
          errorData.message ||
            errorData.details ||
            "שגיאה ביצירת/עדכון השבלונה",
        );
      }

      const data = await response.json();

      if (!data.success) {
        return rejectWithValue(data.message || "שגיאה ביצירת/עדכון השבלונה");
      }

      return data as CreateOrUpdateCustomDocumentResponse;
    } catch (error: any) {
      return rejectWithValue(error.message || "שגיאה בחיבור לשרת");
    }
  },
);

// Thunk לרענון הגדרות מסמכים
export const refreshDocumentSettings = createAsyncThunk<
  GetCustomDocumentSettingsResponse,
  { token: string; companyKey: string },
  { rejectValue: string }
>(
  "documentSettings/refreshDocumentSettings",
  async (request, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${API_URL}Document/GetCustomDocumentSettings`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            accept: "*/*",
          },
          body: JSON.stringify(request),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        return rejectWithValue(
          errorData.message ||
            errorData.details ||
            "שגיאה ברענון הגדרות מסמכים",
        );
      }

      const data = await response.json();

      if (!data.success) {
        return rejectWithValue(data.message || "שגיאה ברענון הגדרות מסמכים");
      }

      return data as GetCustomDocumentSettingsResponse;
    } catch (error: any) {
      return rejectWithValue(error.message || "שגיאה בחיבור לשרת");
    }
  },
);

export const createCustomDocument = createAsyncThunk<
  CreateCustomDocumentResponse,
  CreateCustomDocumentRequest,
  { rejectValue: string }
>(
  "documentSettings/createCustomDocument",
  async (request, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_URL}Document/CreateCustomDocument`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          accept: "*/*",
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const errorData = await response.json();
        return rejectWithValue(
          errorData.message || errorData.details || "שגיאה ביצירת המסמך",
        );
      }

      const data = await response.json();

      if (!data.success) {
        return rejectWithValue(data.message || "שגיאה ביצירת המסמך");
      }

      return data as CreateCustomDocumentResponse;
    } catch (error: any) {
      return rejectWithValue(error.message || "שגיאה בחיבור לשרת");
    }
  },
);

// 🆕 Thunk עבור עדכון סטטוס מסמך
export const updateDocumentStatus = createAsyncThunk<
  UpdateDocumentStatusResponse,
  UpdateDocumentStatusRequest,
  { rejectValue: string }
>(
  "documentSettings/updateDocumentStatus",
  async (request, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_URL}Document/UpdateDocumentStatus`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`,
        );
      }

      const data: UpdateDocumentStatusResponse = await response.json();

      if (!data.success) {
        throw new Error(data.message || "Failed to update document status");
      }

      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || "שגיאה בעדכון סטטוס המסמך");
    }
  },
);
export const generateDocSign = createAsyncThunk<
  GenerateDocSignRequestResponse,
  GenerateDocSignRequest,
  { rejectValue: string }
>("documentSettings/generateDocSign", async (request, { rejectWithValue }) => {
  try {
    const response = await fetch(`${API_URL}Document/GenerateDocSign`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `HTTP error! status: ${response.status}`,
      );
    }

    const data: GenerateDocSignRequestResponse = await response.json();

    if (!data.success) {
      throw new Error(data.message || "Failed to generate doc sign");
    }

    return data;
  } catch (error: any) {
    return rejectWithValue(error.message || "שגיאה בשליחת מסמך לחתימה");
  }
});

// 🆕 Thunk עבור קבלת סטטוס חתימה
export const getDocSignRequest = createAsyncThunk<
  GetDocSignRequestResponse,
  GetDocSignRequest,
  { rejectValue: string }
>(
  "documentSettings/getDocSignRequest",
  async (request, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_URL}Document/getDocSignRequest`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`,
        );
      }

      const data: GetDocSignRequestResponse = await response.json();

      if (!data.success) {
        throw new Error(data.message || "Failed to get doc sign status");
      }

      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || "שגיאה בקבלת סטטוס החתימה");
    }
  },
);

// 🆕 Thunk עבור השלמת תהליך החתימה
export const completeDocSignRequest = createAsyncThunk<
  CompleteDocSignRequestResponse,
  CompleteDocSignRequest,
  { rejectValue: string }
>(
  "documentSettings/completeDocSignRequest",
  async (request, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${API_URL}Document/CompleteDocSignRequest`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(request),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`,
        );
      }

      const data: CompleteDocSignRequestResponse = await response.json();

      if (!data.success) {
        throw new Error(data.message || "Failed to complete doc sign");
      }

      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || "שגיאה בסיום תהליך החתימה");
    }
  },
);

// 🆕 Thunk עבור יצירת מסמך ושליחה לחתימה
export const createDocumentSendSign = createAsyncThunk<
  CreateCustomDocumentResponse,
  CreateDoctSendSignRequest,
  { rejectValue: string }
>(
  "documentSettings/createDocumentSendSign",
  async (request, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${API_URL}Document/CreateCustomDocumentSendSign`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(request),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`,
        );
      }

      const data: CreateCustomDocumentResponse = await response.json();

      if (!data.success) {
        throw new Error(data.message || "Failed to create and send document");
      }

      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || "שגיאה ביצירת ושליחת המסמך");
    }
  },
);
